import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Users.css';
import { IconEdit, IconEye, IconX, IconSearch } from '@tabler/icons-react';

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users/with-rewards');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      success: 'status-success',
      processed: 'status-success',
      approved: 'status-success',
      partial_failed: 'status-warning',
      failed: 'status-failed',
      reward_failed: 'status-failed',
      pending: 'status-pending',
      rejected: 'status-rejected'
    };
    return statusClasses[status] || 'status-pending';
  };

  const openUserDetailModal = (user) => {
    setSelectedUser(user);
  };

  const closeUserDetailModal = () => {
    setSelectedUser(null);
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return !searchQuery || 
      user.username?.toLowerCase().includes(query) ||
      user.vioUserId?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query);
  });

  if (loading) {
    return (
      <div className="users-page">
        <div className="loading-spinner">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Receipt Ops</h1>
        <button className="btn-edit-rules" onClick={() => navigate('/rules')}>
          <IconEdit size={16} stroke={2} />
          Edit Rules Configuration
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className="tab-button"
          onClick={() => navigate('/submissions')}
        >
          Submissions
        </button>
        <button 
          className="tab-button active"
        >
          Users
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Summary Stats */}
      {users.length > 0 && (
        <div className="users-summary-card">
          <h2 className="summary-title">Users Overview</h2>
          <div className="summary-stats-row">
            <div className="summary-stat-item">
              <span className="stat-label">Total Users:</span>
              <span className="stat-value">{users.length}</span>
            </div>
            <div className="summary-stat-item">
              <span className="stat-label">Total Submissions:</span>
              <span className="stat-value">{users.reduce((sum, u) => sum + u.totalSubmissions, 0)}</span>
            </div>
            <div className="summary-stat-item">
              <span className="stat-label">Total Rewards Sent:</span>
              <span className="stat-value">{users.reduce((sum, u) => sum + u.totalRewards, 0)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="users-container">
        <div className="users-controls">
          <div className="search-section">
            <div className="search-box">
              <IconSearch className="search-icon" size={16} stroke={2} />
              <input
                type="text"
                placeholder="Search by username, user ID or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>{users.length === 0 ? 'No Users Yet' : 'No Users Found'}</h3>
            <p>{users.length === 0 ? 'Users will appear here when they sync with the system' : 'Try adjusting your search criteria'}</p>
          </div>
        ) : (
          <>
            <div className="users-table-wrapper">
              <div className="table-header">
                <div className="th-username">Username</div>
                <div className="th-userid">VIO User ID</div>
                <div className="th-email">Email</div>
                <div className="th-submissions">Submissions</div>
                <div className="th-rewards">Rewards</div>
                <div className="th-lastactive">Last Active</div>
                <div className="th-actions">Actions</div>
              </div>
              
              <div className="table-body">
                {filteredUsers.map(user => (
                  <React.Fragment key={user.id}>
                    <div className="table-row-card">
                      <div className="td-username">
                        <span className="username">{user.username || 'N/A'}</span>
                      </div>
                      <div className="td-userid">
                        <span className="user-id">
                          {user.vioUserId || 'N/A'}
                        </span>
                      </div>
                      <div className="td-email">
                        <span className="email">{user.email || '-'}</span>
                      </div>
                      <div className="td-submissions">
                        <span className="count">{user.totalSubmissions}</span>
                      </div>
                      <div className="td-rewards">
                        <span className={`count ${user.totalRewards > 0 ? 'has-rewards' : ''}`}>
                          {user.totalRewards}
                        </span>
                      </div>
                      <div className="td-lastactive">
                        <span className="date-cell">
                          {user.submissions?.[0]?.date 
                            ? formatShortDate(user.submissions[0].date)
                            : formatShortDate(user.createdAt)}
                        </span>
                      </div>
                      <div className="td-actions">
                        <div className="action-buttons-cell">
                          <button
                            className="btn-view-icon"
                            onClick={() => openUserDetailModal(user)}
                            title="View user details"
                          >
                            <IconEye size={16} stroke={2} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={closeUserDetailModal}>
          <div className="modal-content user-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={closeUserDetailModal}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>User Info</h3>
                <div className="detail-grid-left">
                  <div className="detail-item">
                    <label>USERNAME</label>
                    <span>{selectedUser.username || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>VIO USER ID</label>
                    <span>{selectedUser.vioUserId || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>EMAIL</label>
                    <span>{selectedUser.email || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>TOTAL SUBMISSIONS</label>
                    <span className="stat-value-small">{selectedUser.totalSubmissions}</span>
                  </div>
                  <div className="detail-item">
                    <label>TOTAL REWARDS</label>
                    <span className="stat-value-small">{selectedUser.totalRewards}</span>
                  </div>
                  <div className="detail-item">
                    <label>CREATED AT</label>
                    <span>{formatDate(selectedUser.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Submission History ({selectedUser.submissions?.length || 0})</h3>
                <div className="rewards-table-container">
                  <table className="rewards-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Voucher Name</th>
                        <th>Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedUser.submissions || []).map((submission, index) => (
                        <tr key={index} className={submission.hasReward ? '' : 'no-reward-row'}>
                          <td>{formatDate(submission.date)}</td>
                          <td>RM {submission.totalAmount?.toFixed(2) || '0.00'}</td>
                          <td>
                            <div className="reward-types">
                              {submission.voucher && (
                                <span className="reward-type voucher">Voucher</span>
                              )}
                              {submission.tokens && (
                                <span className="reward-type token">Token</span>
                              )}
                              {!submission.voucher && !submission.tokens && (
                                <span className="reward-type none">-</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusBadge(submission.rewardStatus)}`}>
                              {submission.rewardStatus}
                            </span>
                          </td>
                          <td>
                            {submission.voucher ? (
                              <span className="voucher-name">{submission.voucher.name || 'N/A'}</span>
                            ) : (
                              <span className="no-value">-</span>
                            )}
                          </td>
                          <td>
                            {submission.tokens ? (
                              <span className="token-amount">{submission.tokens.amount}</span>
                            ) : (
                              <span className="no-value">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
